import { isMobileDevice } from '@frontend/libs/utils'
import { useEffect, useMemo } from 'react'
import { useCookies } from 'react-cookie'

import type { ProductDetails as TProductDetails } from '../../../libs/types/product'

import Fancybox from '../../../components/fancybox/Fancybox'
import { useProductStore } from '../../../libs/stores/useProductStore'
import ProductGallery from '../components/ProductGallery'
import { ProductImage } from '../components/ProductImage'
import ProductSummary from './ProductSummary'

export interface ProductDetailsProps {
  product: TProductDetails
}

export function ProductDetails({ product }: Readonly<ProductDetailsProps>) {
  const {
    clearSelectedOption,
    selectedOption,
    setDefaultVariation,
    setProduct,
    setSelectedOption,
    variation,
  } = useProductStore((state) => state)
  const [cookies, setCookie] = useCookies(['recently_viewed_products'])

  useEffect(() => {
    if (product) {
      const recentlyViewedProducts: [number] =
        cookies.recently_viewed_products || []
      const index = recentlyViewedProducts.indexOf(product.id)
      if (index !== -1) {
        recentlyViewedProducts.splice(index, 1)
      }
      recentlyViewedProducts.unshift(product.id)
      if (recentlyViewedProducts.length > 10) {
        recentlyViewedProducts.shift()
      }
      setCookie(
        'recently_viewed_products',
        JSON.stringify(recentlyViewedProducts),
        {
          expires: new Date(
            new Date().setFullYear(new Date().getFullYear() + 1),
          ),
          path: '/',
        },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, setCookie, setDefaultVariation, setProduct])

  useEffect(() => {
    setProduct(product)
    if (product.type === 'variable') {
      setDefaultVariation(product)
    }
  }, [product, setDefaultVariation, setProduct])

  const galleryImages = useMemo(() => {
    if (variation && variation.gallery.length > 0) {
      return variation.gallery || []
    }
    if (product?.type === 'variable' || product?.type === 'simple') {
      return product?.gallery || []
    }
    return []
  }, [product, variation])

  const { image, packshotImage } = product.type === 'variable' ? product : {}

  const mobileGalleryImages = [
    variation?.image || packshotImage || image,
    ...(Array.isArray(galleryImages) ? galleryImages : []),
  ]

  const uniqueMobileGalleryImages = mobileGalleryImages.filter(
    (image, index, self) =>
      index === self.findIndex((img) => img.id === image.id),
  )

  return (
    <div className="grid grid-cols-1 gap-4 md:mt-6 md:grid-cols-12 md:gap-12">
      <div className="-mx-9 mt-5 md:col-span-8 md:mx-0 md:mt-0">
        {isMobileDevice && (
          <div className="block md:hidden">
            <ProductGallery images={uniqueMobileGalleryImages} />
          </div>
        )}
        {!isMobileDevice && (
          <Fancybox
            options={{
              Carousel: {
                infinite: false,
              },
            }}
          >
            <div className="hidden grid-cols-1 gap-4 md:grid md:grid-cols-2">
              <div className="relative">
                <div className="sticky top-10">
                  <a
                    data-fancybox="gallery"
                    href={
                      variation?.image?.sizes?.full ||
                      packshotImage?.sizes?.full ||
                      image?.sizes?.full
                    }
                  >
                    <ProductImage
                      image={variation?.image || packshotImage || image}
                    />
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {galleryImages.map((image) => (
                  <a
                    className="relative block"
                    data-fancybox="gallery"
                    href={image.sizes.full}
                    key={image.id}
                  >
                    <ProductImage image={image} />
                  </a>
                ))}
              </div>
            </div>
          </Fancybox>
        )}
      </div>
      <div className="md:col-span-4">
        <ProductSummary
          onClearSelection={clearSelectedOption}
          onSelectOption={setSelectedOption}
          product={product}
          selectedOption={selectedOption}
          showMeta
          variation={variation}
        />
      </div>
    </div>
  )
}

export default ProductDetails
